import { step, type Direction, type TilePos } from './geometry.js';
import { type CollisionGrid } from './grid.js';

export type StepRejection = 'blocked' | 'too-fast';

export type StepResult =
  | { readonly ok: true; readonly to: TilePos }
  | { readonly ok: false; readonly reason: StepRejection };

export interface StepRequest {
  readonly from: TilePos;
  readonly direction: Direction;
  /** Server time (ms) of this request. */
  readonly now: number;
  /** Server time (ms) of the previous accepted step, or `undefined` for the first step. */
  readonly lastStepAt: number | undefined;
  /** Walking speed; a sprinting step may come `SPRINT_SPEED_MULTIPLIER` times as fast. */
  readonly tilesPerSecond: number;
  /** Whether the player holds the sprint key for this step. */
  readonly sprint?: boolean;
}

/** How much faster a sprinting player moves than a walking one. */
export const SPRINT_SPEED_MULTIPLIER = 1.75;

/** Tiles per second of a step, walking or sprinting. */
export function stepTilesPerSecond(tilesPerSecond: number, sprint: boolean): number {
  return sprint ? tilesPerSecond * SPRINT_SPEED_MULTIPLIER : tilesPerSecond;
}

/**
 * Small tolerance (ms) on the step interval so normal network jitter does not reject legitimate input.
 * The client paces itself at exactly 1000 / tilesPerSecond; bursts beyond this tolerance are rejected.
 */
export const STEP_INTERVAL_TOLERANCE_MS = 25;

export function minStepIntervalMs(tilesPerSecond: number): number {
  if (!(tilesPerSecond > 0)) throw new RangeError('tilesPerSecond must be positive');
  return 1000 / tilesPerSecond;
}

/**
 * Authoritative single-step validation (spec §6.4). The server never trusts client positions:
 * it applies the requested direction to its own position for the player.
 */
export function validateStep(grid: CollisionGrid, request: StepRequest): StepResult {
  if (request.lastStepAt !== undefined) {
    const elapsed = request.now - request.lastStepAt;
    if (
      elapsed + STEP_INTERVAL_TOLERANCE_MS <
      minStepIntervalMs(stepTilesPerSecond(request.tilesPerSecond, request.sprint === true))
    ) {
      return { ok: false, reason: 'too-fast' };
    }
  }
  const to = step(request.from, request.direction);
  if (!grid.isWalkable(to)) return { ok: false, reason: 'blocked' };
  return { ok: true, to };
}
